import { isEmpty } from 'lodash'
import {
  ConditionalInputs,
  ConditionalItems,
  CONDITIONAL_ITEM_TYPE,
  EvtInput,
  Parameters,
  PopulatedEvtInput,
} from './Common.types'

export const getExistedValue = (
  seletedItems: number[],
  itemsList: { id: number }[],
) =>
  seletedItems.filter(selectedItem =>
    itemsList.some(({ id }) => id === selectedItem),
  )

export const isShowConditionalTarget = (parameters?: Parameters) =>
  Boolean(parameters?.conditional_logic?.conditions)

export const isEvtInputResultEmpty = (result: EvtInput['result']) => {
  if (result === undefined || result === null) {
    return true
  }

  if (Array.isArray(result)) {
    return result.length === 0
  }

  return result === ''
}

export const applyPopulatedEvtInputsToEvtInputs = (
  evtInputs: EvtInput[] = [],
  populatedEvtInputs: PopulatedEvtInput[] = [],
): EvtInput[] => {
  if (!populatedEvtInputs.length) {
    return evtInputs
  }

  const nextEvtInputs = [...evtInputs]

  populatedEvtInputs.forEach(
    ({ id: populatedEvtInputId, input: inputId, result: populatedResult }) => {
      const existingIndex = nextEvtInputs.findIndex(
        item => item.id === populatedEvtInputId || item.input === inputId,
      )

      if (existingIndex >= 0) {
        const existingEvtInput = nextEvtInputs[existingIndex]

        if (isEvtInputResultEmpty(existingEvtInput.result)) {
          nextEvtInputs[existingIndex] = {
            ...existingEvtInput,
            id: populatedEvtInputId,
            input: inputId,
            result: populatedResult,
          }
        }

        return
      }

      nextEvtInputs.push({
        id: populatedEvtInputId,
        input: inputId,
        result: populatedResult,
        uuid: '',
      })
    },
  )

  return nextEvtInputs
}

export const clearPopulatedEvtInputsFromEvtInputs = (
  evtInputs: EvtInput[] = [],
  clearedPopulatedEvtInputIds: number[] = [],
): EvtInput[] => {
  if (!clearedPopulatedEvtInputIds.length) {
    return evtInputs
  }

  const clearedIds = new Set(clearedPopulatedEvtInputIds)

  return evtInputs.map(evtInput => {
    if (!clearedIds.has(evtInput.id)) {
      return evtInput
    }

    const clearedResult = Array.isArray(evtInput.result) ? [] : null

    return {
      ...evtInput,
      result: clearedResult,
    }
  })
}

export const canRenderConditionalInputOrGroup = (
  evt_inputs: EvtInput[],
  parameters: Parameters,
  availability: { evt_tasks: number[] },
  evtTaskId: number,
  id: number,
  propName: CONDITIONAL_ITEM_TYPE,
) => {
  if (!isShowConditionalTarget(parameters)) {
    return false
  }

  const isAvailable = evt_inputs?.some(
    ({ conditionalItems }) =>
      conditionalItems &&
      conditionalItems[propName]?.available?.some(inputId => inputId === id),
  )

  const isHidden = evt_inputs?.some(
    ({ conditionalItems }) =>
      conditionalItems &&
      conditionalItems[propName]?.hidden?.some(inputId => inputId === id),
  )

  const hasConditionalItems = evt_inputs?.some(
    ({ conditionalItems }) => !!conditionalItems && (isAvailable || isHidden),
  )

  return hasConditionalItems
    ? !isAvailable
    : isEmpty(availability) || !availability.evt_tasks.includes(evtTaskId)
}

const mergePopulatedEvtInputs = (
  existing: PopulatedEvtInput[] = [],
  incoming: PopulatedEvtInput[] = [],
) => {
  if (!incoming.length) {
    return existing
  }

  const merged = new Map<number, PopulatedEvtInput>()

  existing.forEach(item => {
    merged.set(item.id, item)
  })
  incoming.forEach(item => {
    merged.set(item.id, item)
  })

  return Array.from(merged.values())
}

export const mergeConditionalItems = (
  existing?: ConditionalItems,
  incoming?: ConditionalItems,
): ConditionalItems | undefined => {
  if (!incoming) {
    return existing
  }

  if (!existing) {
    return incoming
  }

  return {
    inputs: incoming.inputs ?? existing.inputs,
    inputGroups: incoming.inputGroups ?? existing.inputGroups,
    populatedEvtInputs: mergePopulatedEvtInputs(
      existing.populatedEvtInputs,
      incoming.populatedEvtInputs,
    ),
    clearedConditionalEvtInputIds: [
      ...new Set([
        ...(existing.clearedConditionalEvtInputIds || []),
        ...(incoming.clearedConditionalEvtInputIds || []),
      ]),
    ],
    clearedPopulatedEvtInputIds: [
      ...new Set([
        ...(existing.clearedPopulatedEvtInputIds || []),
        ...(incoming.clearedPopulatedEvtInputIds || []),
      ]),
    ],
  }
}

export const getConditionalItemsIds = (inputs?: ConditionalInputs) => {
  const { available, hidden } = inputs || {}

  return [...(available || []), ...(hidden || [])]
}

export const filterConditionalItems = (
  inputsIds: number[],
  newInputsIds: number[],
) => inputsIds.filter(id => !newInputsIds.includes(id))
